import {Component, Input} from '@angular/core';
import {RiskHeatmapPoint} from '@core/types';

@Component({
  selector: 'core-heat-map-widget',
  imports: [],
  templateUrl: './heat-map-widget.html',
  styleUrl: './heat-map-widget.scss',
  host: {
    style: 'display: inline-flex;height: 100%;width: 100%;flex-direction: row;gap: 0.625rem;'
  }
})
export class HeatMapWidget {
  @Input()
  labels!: {x: string, y: string};
  @Input()
  attributes!: {x: string[], y: string[]};
  @Input()
  points: Array<RiskHeatmapPoint & {badge?: string | number; tooltip?: string}> = [];

  private _values!: number[][];

  get values(): number[][] {
    return this._values;
  }

  @Input()
  set values(value: number[][]) {
    const normalized = Array.isArray(value) && value.length
      ? value
          .map(row => Array.isArray(row) ? row : [])
          .filter(row => row.length > 0)
      : [];
    this._values = normalized.length ? normalized : [[0]];
  }

  get renderedRows(): Array<{track: string; logicalRowIndex: number; cells: number[]}> {
    const rows = this.values || [];
    const rendered: Array<{track: string; logicalRowIndex: number; cells: number[]}> = [];
    for (let logicalRowIndex = rows.length - 1; logicalRowIndex >= 0; logicalRowIndex--) {
      rendered.push({
        track: `row_${logicalRowIndex}`,
        logicalRowIndex,
        cells: rows[logicalRowIndex] || [],
      });
    }
    return rendered;
  }

  get renderedYAttributes(): string[] {
    const attrs = this.attributes?.y || [];
    return [...attrs].reverse();
  }

  get renderedPoints(): Array<{
    key: string;
    left: number;
    top: number;
    badge: string;
    tooltip: string;
  }> {
    const xLevels = Math.max(1, this.attributes?.x?.length || 1);
    const yLevels = Math.max(1, this.attributes?.y?.length || 1);
    const buckets = new Map<string, number>();
    const points = Array.isArray(this.points) ? this.points : [];

    return points
      .map((point, index) => {
        const impact = this.boundLevel(this.toNumber(point?.impact), xLevels);
        const probability = this.boundLevel(this.toNumber(point?.probability), yLevels);
        const bucketKey = `${impact}_${probability}`;
        const bucketIndex = buckets.get(bucketKey) || 0;
        buckets.set(bucketKey, bucketIndex + 1);
        const baseLeft = ((impact - 0.5) / xLevels) * 100;
        const baseTop = (1 - ((probability - 0.5) / yLevels)) * 100;
        const jitterStep = 3.2;
        const jitterAngle = (bucketIndex % 8) * (Math.PI / 4);
        const ring = Math.floor(bucketIndex / 8);
        const jitter = bucketIndex === 0 ? 0 : jitterStep * (ring + 1);
        const left = this.boundPercent(baseLeft + (Math.cos(jitterAngle) * jitter));
        const top = this.boundPercent(baseTop + (Math.sin(jitterAngle) * jitter));
        const badge = String(point?.badge ?? index + 1);
        const tooltip = String(point?.tooltip || point?.conceptName || `Concepto ${badge}`);

        return {
          key: `${point?.conceptId || 'concept'}_${point?.crimeId || 'crime'}_${index}`,
          left,
          top,
          badge,
          tooltip,
        };
      });
  }

  isGreenCell(row: number, col: number): boolean {
    return this.riskBand(row, col) === 'low';
  }

  isYellowCell(row: number, col: number): boolean {
    return this.riskBand(row, col) === 'medium';
  }

  isRedCell(row: number, col: number): boolean {
    return this.riskBand(row, col) === 'high';
  }

  private riskBand(row: number, col: number): 'low' | 'medium' | 'high' {
    const yLevels = Math.max(1, this.attributes?.y?.length || this.values.length || 1);
    const xLevels = Math.max(1, this.attributes?.x?.length || this.values?.[0]?.length || 1);

    const yLevel = Math.min(yLevels, Math.max(1, row + 1));
    const xLevel = Math.min(xLevels, Math.max(1, col + 1));
    const score = (xLevel + yLevel) / 2;

    if (score <= 2) return 'low';
    if (score <= 3.5) return 'medium';
    return 'high';
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private boundLevel(value: number, max: number): number {
    return Math.max(1, Math.min(max, Math.round(value)));
  }

  private boundPercent(value: number): number {
    return Math.max(4, Math.min(96, value));
  }
}
